/**
 * Timeline Entity - Event history for applications
 * 
 * Per user decision D5: Track all form submissions, explicitly mark major status changes.
 * Timeline events are immutable once created.
 */
import { z } from 'zod';
import { TimelineEventId, ApplicationId, UserId } from '../types/branded';
import { UserRole } from '../types/enums';

// ============================================================================
// Timeline Event Entity (Projection/Read Model)
// ============================================================================

export const TimelineEventSchema = z.object({
  id: TimelineEventId,
  applicationId: ApplicationId,
  
  // Event details
  eventType: z.string(), // e.g., 'application.submitted', 'status.changed'
  
  // Per user decision D5: Explicitly mark major status changes
  isMajor: z.boolean(),
  
  // Timing
  occurredAt: z.number().int(), // Unix seconds
  
  // Actor (who triggered the event)
  actorId: UserId.nullable().optional(), // null for system events
  actorRole: UserRole.nullable().optional(),
  
  // Human-readable summary
  summary: z.string(),
  
  // Structured metadata (for filtering/display)
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type TimelineEvent = z.infer<typeof TimelineEventSchema>;

// ============================================================================
// Major Event Types (per user decision D5)
// These events are marked with isMajor: true
// ============================================================================

export const MAJOR_EVENT_TYPES = [
  'application.submitted',
  'application.status_changed',
  'application.assigned',
  'application.reassigned',
  'application.interview_requested',
  'application.interview_scheduled',
  'application.visit_scheduled',
  'application.interview_completed',
] as const;

export type MajorEventType = typeof MAJOR_EVENT_TYPES[number];

/** Check if an event type is considered major */
export const isMajorEventType = (eventType: string): eventType is MajorEventType =>
  (MAJOR_EVENT_TYPES as readonly string[]).includes(eventType);

// ============================================================================
// All Event Types
// ============================================================================

export const EVENT_TYPES = {
  // Major events (status changes)
  APPLICATION_CREATED: 'application.created',
  APPLICATION_SUBMITTED: 'application.submitted',
  APPLICATION_STATUS_CHANGED: 'application.status_changed',
  APPLICATION_ASSIGNED: 'application.assigned',
  APPLICATION_REASSIGNED: 'application.reassigned',
  APPLICATION_INTERVIEW_REQUESTED: 'application.interview_requested',
  APPLICATION_INTERVIEW_SCHEDULED: 'application.interview_scheduled',
  APPLICATION_VISIT_SCHEDULED: 'application.visit_scheduled',
  APPLICATION_INTERVIEW_COMPLETED: 'application.interview_completed',
  
  // Minor events (form updates)
  APPLICATION_UPDATED: 'application.updated',
  CHILD_ADDED: 'application.child_added',
  CHILD_UPDATED: 'application.child_updated',
  CHILD_REMOVED: 'application.child_removed',
  
  // Related entity events
  NOTE_ADDED: 'note.added',
  NOTE_UPDATED: 'note.updated',
  NOTE_DELETED: 'note.deleted',
  DOCUMENT_UPLOADED: 'document.uploaded',
  DOCUMENT_DELETED: 'document.deleted',
  MESSAGE_SENT: 'message.sent',
  CONTACT_LOGGED: 'contact.logged',
  CONTACT_UPDATED: 'contact.updated',
  HEALTH_INFO_UPDATED: 'health_info.updated',
} as const;

export type EventType = typeof EVENT_TYPES[keyof typeof EVENT_TYPES];

// ============================================================================
// Timeline Query Types
// ============================================================================

export const TimelineFilterSchema = z.object({
  applicationId: ApplicationId,
  eventTypes: z.array(z.string()).optional(),
  majorOnly: z.boolean().optional(),
  startDate: z.number().int().optional(),
  endDate: z.number().int().optional(),
  actorId: UserId.optional(),
});
export type TimelineFilter = z.infer<typeof TimelineFilterSchema>;
